import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import { digObject, formatDate } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import ToggleListItem from '@components/ToggleListItem'

import useBulkUpdateProviderPriceListsModal from './hooks/useBulkUpdateProviderPriceListsModal'

const MODAL_KEY = 'BulkUpdateProviderPriceListsModal'

export type ModalCallbacks = {
  bulkUpdateProviderPriceLists: (payload: HandleCallbackActionParams) => void,
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const BulkUpdateProviderPricelistsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    callbacks: {
      deselectAll,
    },
    providerPriceList,
    priceListIds,
  } = modalPayload

  const hookPayload = useBulkUpdateProviderPriceListsModal(
    { ...callbacks, deselectAll },
    providerPriceList,
  )

  const {
    callbacks: {
      handleSubmit,
    },
    currentDateTime,
    entityState: {
      direction,
      expiresAt,
      percentage,
      roundToNearestDollar,
    },
    handlers,
    saveEnabled,
    setEntityState,
    updating,
  } = hookPayload

  const defaultOptions = [
    { value: '', label: 'Please Select...' },
    { value: 'increase', label: 'Increase by' },
    { value: 'decrease', label: 'Decrease by' },
  ]

  const options = [...defaultOptions,
    { value: 'undoIncrease', label: 'Undo Increase of' },
    { value: 'undoDecrease', label: 'Undo Decrease of' },
  ]

  const selectOptions = roundToNearestDollar ? defaultOptions : options

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Bulk Update" titleSecondLine="Price Lists" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="Expiry Date">
            <input name="expiresAt" min={currentDateTime} type="date" value={expiresAt} {...handlers} />
          </Form.Field>

          {expiresAt && (
            <SidebarNoticeBox boxProps={{ marginTop: 'large', marginBottom: 'none' }}>
              <SidebarNoticeBox.Title>
                Selected price lists will expire on {formatDate(expiresAt, 'yyyy-MM-dd', 'dd LLL yyyy')}
              </SidebarNoticeBox.Title>

              <SidebarNoticeBox.Paragraph>
                Price changes will come into effect after the expiry date.
              </SidebarNoticeBox.Paragraph>
            </SidebarNoticeBox>
          )}

          <ToggleListItem
            boxProps={{ marginTop: 'large', marginBottom: 'large' }}
            onClick={() => setEntityState({ roundToNearestDollar: !roundToNearestDollar })}
            toggleValue={roundToNearestDollar}
          >
            <ToggleListItem.Title>Round to nearest dollar</ToggleListItem.Title>

            <ToggleListItem.Description>
              This action will round prices to the nearest dollar.
            </ToggleListItem.Description>
          </ToggleListItem>

          <Form.Field label="* Adjust" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select name="direction" value={direction} {...handlers}>
              {selectOptions.map(option => (
                <option value={option.value}> {option.label} </option>
              ))}
            </select>
          </Form.Field>

          {direction && (
            <Form.Field label="* Percentage" marginTop="large">
              <input
                min="0"
                name="percentage"
                step="0.1"
                type="number"
                value={percentage}
                {...handlers}
              />
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          onClick={() => handleSubmit(priceListIds)}
          size="large"
        >
          {saveEnabled ? 'Update Price Lists' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <BulkUpdateProviderPricelistsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
